import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  MapPin, Phone, Mail, Star, Clock, CheckCircle, Shield, 
  Award, Briefcase, Calendar, ArrowLeft, Loader2,
  Droplet, Zap, Thermometer, Wrench, Sparkles, Car, BadgeCheck,
  CalendarCheck, DollarSign, Home, Bug, Hammer, Trees, Truck, DoorOpen, Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  plumbing: Droplet,
  electrical: Zap,
  hvac: Thermometer,
  handyman: Wrench,
  cleaning: Sparkles,
  mechanics: Car,
  roofing: Home,
  "pest-control": Bug,
  remodeling: Hammer,
  landscaping: Trees,
  moving: Truck,
  "garage-door": DoorOpen,
  "appliance-repair": Settings,
};

const ProfilePage = () => {
  const { workerId } = useParams();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [platformFee, setPlatformFee] = useState(10);
  const [reviewForm, setReviewForm] = useState({
    customer_name: "",
    customer_email: "",
    rating: 5,
    comment: "",
    service_category: ""
  });
  const [bookingForm, setBookingForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    agreed_price: "",
    scheduled_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchProfile();
    fetchPlatformFee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/${workerId}`);
      setProfile(response.data.profile);
      setReviews(response.data.reviews);
      if (response.data.profile.services?.length > 0) {
        setReviewForm(prev => ({ ...prev, service_category: response.data.profile.services[0] }));
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformFee = async () => {
    try {
      const response = await axios.get(`${API}/admin/pricing`);
      setPlatformFee(response.data.platform_fee_percent || 10);
    } catch (error) {
      console.error("Error fetching platform fee:", error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    
    try {
      await axios.post(`${API}/reviews`, {
        worker_id: workerId,
        ...reviewForm
      });
      toast.success("Review submitted successfully!");
      setShowReviewModal(false);
      setReviewForm({ customer_name: "", customer_email: "", rating: 5, comment: "", service_category: profile.services[0] || "" });
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setSubmittingBooking(true);
    
    try {
      const response = await axios.post(`${API}/bookings`, {
        job_id: `direct-booking-${Date.now()}`,
        worker_id: workerId,
        customer_name: bookingForm.customer_name,
        customer_email: bookingForm.customer_email,
        customer_phone: bookingForm.customer_phone,
        agreed_price: parseFloat(bookingForm.agreed_price),
        scheduled_date: bookingForm.scheduled_date || null,
        notes: bookingForm.notes || null
      });
      
      toast.success("Booking request sent! The contractor will be notified.");
      setShowBookingModal(false);
      setBookingForm({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        agreed_price: "",
        scheduled_date: "",
        notes: ""
      });
      
      // Optionally redirect to payment
      if (response.data.booking_id) {
        toast.info(`Contractor payout: $${response.data.contractor_payout} (after ${platformFee}% platform fee)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmittingBooking(false);
    }
  };

  const calculateFees = () => {
    const price = parseFloat(bookingForm.agreed_price) || 0;
    const fee = (price * platformFee / 100).toFixed(2);
    const payout = (price - parseFloat(fee)).toFixed(2);
    return { fee, payout };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container-custom py-20 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a73e8]" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container-custom py-20 text-center">
          <h1 className="text-2xl font-medium text-[#202124] mb-4">Profile Not Found</h1>
          <Link to="/browse" className="text-[#1a73e8] hover:underline">
            Browse all contractors
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="profile-page">
      <Navbar />

      {/* Back Button */}
      <div className="bg-[#f8f9fa] border-b border-[#dadce0]">
        <div className="container-custom py-4">
          <Link to="/browse" className="inline-flex items-center text-[#5f6368] hover:text-[#202124] transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to contractors
          </Link>
        </div>
      </div>

      {/* Profile Header */}
      <section className="py-8 bg-[#f8f9fa]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Profile Photo */}
            <div className="w-32 h-32 rounded-2xl bg-[#e8f0fe] flex items-center justify-center overflow-hidden shrink-0">
              {profile.profile_photo ? (
                <img src={profile.profile_photo} alt={profile.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl font-medium text-[#1a73e8]">
                  {profile.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-medium text-[#202124]">
                  {profile.business_name || profile.name}
                </h1>
                {profile.is_verified && (
                  <span className="badge-verified" data-testid="verified-badge">
                    <BadgeCheck className="w-4 h-4" />
                    Verified
                  </span>
                )}
                {profile.is_featured && (
                  <span className="bg-[#1a73e8] text-white px-2 py-1 rounded-md text-xs font-medium">
                    Featured Pro
                  </span>
                )}
              </div>

              {profile.business_name && (
                <p className="text-[#5f6368] mb-2">{profile.name}</p>
              )}

              {/* Rating */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-[#f9ab00] fill-[#f9ab00]" />
                  <span className="font-medium text-[#202124]">{profile.rating?.toFixed(1) || "New"}</span>
                  <span className="text-[#5f6368]">({profile.reviews_count} reviews)</span>
                </div>
                <div className="flex items-center gap-1 text-[#5f6368]">
                  <Briefcase className="w-4 h-4" />
                  <span>{profile.jobs_completed} jobs completed</span>
                </div>
              </div>

              {/* Services */}
              <div className="flex flex-wrap gap-2 mb-4">
                {profile.services?.map((service) => {
                  const Icon = serviceIcons[service];
                  return (
                    <span key={service} className="flex items-center gap-1 px-3 py-1 bg-[#e8f0fe] text-[#1a73e8] rounded-full text-sm">
                      {Icon && <Icon className="w-4 h-4" />}
                      {service.charAt(0).toUpperCase() + service.slice(1)}
                    </span>
                  );
                })}
              </div>

              {/* Verification Badges */}
              <div className="flex flex-wrap gap-3">
                {profile.is_licensed && (
                  <span className="flex items-center gap-1 text-sm text-[#1e8e3e]">
                    <Award className="w-4 h-4" />
                    Licensed
                  </span>
                )}
                {profile.is_background_checked && (
                  <span className="flex items-center gap-1 text-sm text-[#1e8e3e]">
                    <Shield className="w-4 h-4" />
                    Background Checked
                  </span>
                )}
                {profile.is_insured && (
                  <span className="flex items-center gap-1 text-sm text-[#1e8e3e]">
                    <CheckCircle className="w-4 h-4" />
                    Insured
                  </span>
                )}
              </div>
            </div>

            {/* Contact Card */}
            <div className="w-full md:w-auto bg-white rounded-2xl border border-[#dadce0] p-6 shadow-sm">
              <div className="text-center mb-4">
                <div className="text-2xl font-bold text-[#202124]">${profile.hourly_rate}/hr</div>
                <p className="text-sm text-[#5f6368]">Starting rate</p>
              </div>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-[#5f6368]" />
                  <span className="text-[#202124]">{profile.location}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-[#5f6368]" />
                  <span className="text-[#202124]">Responds {profile.response_time}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-[#5f6368]" />
                  <span className="text-[#202124]">{profile.experience_years} years experience</span>
                </div>
              </div>

              {/* Book Now Button - Primary CTA */}
              <Button
                onClick={() => setShowBookingModal(true)}
                className="w-full py-3 bg-[#1e8e3e] hover:bg-[#137333] text-white rounded-full font-medium mb-2"
                data-testid="book-now-btn"
              >
                <CalendarCheck className="w-4 h-4 mr-2" />
                Book Now
              </Button>

              <a
                href={`tel:${profile.phone}`}
                className="flex items-center justify-center gap-2 w-full py-3 bg-[#1a73e8] text-white rounded-full font-medium hover:bg-[#1557b0] transition-colors mb-2"
                data-testid="call-contractor-btn"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
              
              <Button
                variant="outline"
                className="w-full rounded-full border-[#dadce0]"
                onClick={() => window.location.href = `mailto:${profile.email || ''}`}
                data-testid="email-contractor-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Left Column - About & Photos */}
            <div className="md:col-span-2 space-y-8">
              {/* About */}
              <div>
                <h2 className="text-xl font-medium text-[#202124] mb-4">About</h2>
                <p className="text-[#5f6368] leading-relaxed">{profile.description}</p>
              </div>

              {/* Service Areas */}
              {profile.service_areas?.length > 0 && (
                <div>
                  <h2 className="text-xl font-medium text-[#202124] mb-4">Service Areas</h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.service_areas.map((area) => (
                      <span key={area} className="px-3 py-1 bg-[#f8f9fa] text-[#5f6368] rounded-full text-sm">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Work Photos */}
              {profile.work_photos?.length > 0 && (
                <div>
                  <h2 className="text-xl font-medium text-[#202124] mb-4">Past Work</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {profile.work_photos.map((photo, idx) => (
                      <img
                        key={idx}
                        src={photo}
                        alt={`Work sample ${idx + 1}`}
                        className="w-full h-40 object-cover rounded-xl"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Reviews */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-medium text-[#202124]">
                    Reviews ({reviews.length})
                  </h2>
                  <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="rounded-full border-[#dadce0]" data-testid="write-review-btn">
                        Write a Review
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Write a Review</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmitReview} className="space-y-4">
                        <div>
                          <Label>Your Name</Label>
                          <Input
                            value={reviewForm.customer_name}
                            onChange={(e) => setReviewForm({...reviewForm, customer_name: e.target.value})}
                            required
                            data-testid="review-name-input"
                          />
                        </div>
                        <div>
                          <Label>Your Email</Label>
                          <Input
                            type="email"
                            value={reviewForm.customer_email}
                            onChange={(e) => setReviewForm({...reviewForm, customer_email: e.target.value})}
                            required
                            data-testid="review-email-input"
                          />
                        </div>
                        <div>
                          <Label>Rating</Label>
                          <div className="flex gap-2 mt-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setReviewForm({...reviewForm, rating: star})}
                                className="p-1"
                                data-testid={`rating-star-${star}`}
                              >
                                <Star
                                  className={`w-6 h-6 ${
                                    star <= reviewForm.rating
                                      ? "text-[#f9ab00] fill-[#f9ab00]"
                                      : "text-gray-300"
                                  }`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label>Service</Label>
                          <select
                            value={reviewForm.service_category}
                            onChange={(e) => setReviewForm({...reviewForm, service_category: e.target.value})}
                            className="w-full h-10 px-3 rounded-md border border-[#dadce0]"
                            required
                            data-testid="review-service-select"
                          >
                            {profile.services?.map((service) => (
                              <option key={service} value={service}>
                                {service.charAt(0).toUpperCase() + service.slice(1)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label>Your Review</Label>
                          <Textarea
                            value={reviewForm.comment}
                            onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                            rows={4}
                            required
                            data-testid="review-comment-input"
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={submittingReview}
                          className="w-full bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
                          data-testid="submit-review-btn"
                        >
                          {submittingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Review"}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                {reviews.length === 0 ? (
                  <div className="text-center py-8 bg-[#f8f9fa] rounded-xl">
                    <Star className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-[#5f6368]">No reviews yet. Be the first to leave one!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="p-4 bg-[#f8f9fa] rounded-xl" data-testid={`review-${review.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-[#202124]">{review.customer_name}</p>
                            <div className="flex items-center gap-1 mt-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < review.rating
                                      ? "text-[#f9ab00] fill-[#f9ab00]"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <span className="text-sm text-[#5f6368]">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-[#5f6368]">{review.comment}</p>
                        <span className="inline-block mt-2 text-xs text-[#5f6368] bg-white px-2 py-1 rounded">
                          {review.service_category}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Stats */}
            <div className="space-y-6">
              <div className="bg-[#f8f9fa] rounded-2xl p-6">
                <h3 className="font-medium text-[#202124] mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-[#5f6368]">Jobs Completed</span>
                    <span className="font-medium text-[#202124]">{profile.jobs_completed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5f6368]">Years Experience</span>
                    <span className="font-medium text-[#202124]">{profile.experience_years}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5f6368]">Response Time</span>
                    <span className="font-medium text-[#202124]">{profile.response_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5f6368]">Available</span>
                    <span className={`font-medium ${profile.is_available ? "text-[#1e8e3e]" : "text-[#d93025]"}`}>
                      {profile.is_available ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Request Service CTA */}
              <div className="bg-[#e8f0fe] rounded-2xl p-6 text-center">
                <h3 className="font-medium text-[#202124] mb-2">Need this service?</h3>
                <p className="text-sm text-[#5f6368] mb-4">
                  Get a free quote from {profile.name}
                </p>
                <Link
                  to="/request"
                  className="inline-block w-full py-3 bg-[#1a73e8] text-white rounded-full font-medium hover:bg-[#1557b0] transition-colors"
                  data-testid="request-service-cta"
                >
                  Request a Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-[#1e8e3e]" />
              Book {profile?.business_name || profile?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitBooking} className="space-y-4">
            <div>
              <Label>Your Name *</Label>
              <Input
                value={bookingForm.customer_name}
                onChange={(e) => setBookingForm({...bookingForm, customer_name: e.target.value})}
                placeholder="John Smith"
                required
                className="mt-1"
                data-testid="booking-name-input"
              />
            </div>
            <div>
              <Label>Your Email *</Label>
              <Input
                type="email"
                value={bookingForm.customer_email}
                onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})}
                placeholder="john@example.com"
                required
                className="mt-1"
                data-testid="booking-email-input"
              />
            </div>
            <div>
              <Label>Your Phone *</Label>
              <Input
                type="tel"
                value={bookingForm.customer_phone}
                onChange={(e) => setBookingForm({...bookingForm, customer_phone: e.target.value})}
                placeholder="(555) 123-4567"
                required
                className="mt-1"
                data-testid="booking-phone-input"
              />
            </div>
            <div>
              <Label>Agreed Service Price ($) *</Label>
              <Input
                type="number"
                min="1"
                step="0.01"
                value={bookingForm.agreed_price}
                onChange={(e) => setBookingForm({...bookingForm, agreed_price: e.target.value})}
                placeholder="150.00"
                required
                className="mt-1"
                data-testid="booking-price-input"
              />
              {bookingForm.agreed_price && parseFloat(bookingForm.agreed_price) > 0 && (
                <div className="mt-2 p-3 bg-[#f8f9fa] rounded-lg text-sm">
                  <div className="flex justify-between text-[#5f6368]">
                    <span>Service Total:</span>
                    <span>${parseFloat(bookingForm.agreed_price).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#5f6368] mt-1">
                    <span>Platform Fee ({platformFee}%):</span>
                    <span>-${calculateFees().fee}</span>
                  </div>
                  <div className="flex justify-between font-medium text-[#202124] mt-1 pt-2 border-t border-[#dadce0]">
                    <span>Contractor Receives:</span>
                    <span className="text-[#1e8e3e]">${calculateFees().payout}</span>
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label>Preferred Date (Optional)</Label>
              <Input
                type="date"
                value={bookingForm.scheduled_date}
                onChange={(e) => setBookingForm({...bookingForm, scheduled_date: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
                data-testid="booking-date-input"
              />
            </div>
            <div>
              <Label>Job Description / Notes</Label>
              <Textarea
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                placeholder="Describe the work needed..."
                rows={3}
                className="mt-1"
                data-testid="booking-notes-input"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowBookingModal(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingBooking}
                className="bg-[#1e8e3e] hover:bg-[#137333] rounded-full"
                data-testid="submit-booking-btn"
              >
                {submittingBooking ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-1" />
                    Confirm Booking
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ProfilePage;
